/**
 * Onde o tocador de música parou, para ele voltar como estava.
 *
 * O audiolivro já fazia isto -- `telas/livros/Ouvir.tsx`, nos dois apps --, e a
 * música não, porque música era faixa de três minutos. Deixou de ser: com o
 * disco inteiro em mãos, um show do `etree` tem vinte faixas e duas horas e
 * meia. Fechar o app na faixa catorze e voltar ao nada é a mesma perda que o
 * audiolivro já sabia não infligir.
 *
 * ── Por que o cartaz inteiro, e não só um endereço ────────────────────────
 *
 * O tocador do audiolivro mora numa *tela*: ao voltar, a tela já está de pé e
 * já foi buscar o livro, e ao guardado bastam duas coisas -- em que capítulo, e
 * em que segundo. O tocador de música não tem tela: ele é um provedor acima de
 * todas elas, e ao voltar não há nada. O guardado tem de trazer também *o que*
 * estava tocando.
 *
 * E isso é o cartaz inteiro, não um `iaId` para ir buscar de novo:
 *
 *   - remontá-lo custaria uma volta ao Internet Archive a cada vez que o app
 *     abre, para uma barra que talvez ninguém vá tocar;
 *   - e nem assim voltaria igual. Metade do cartaz vem do Last.fm -- o título
 *     que a pessoa reconhece, o artista, a capa -- e o acervo não tem como
 *     inventá-los: ele escreve `GYMNOPÉDIE No. 1 - Boston Symphony Orchestra`.
 *
 * O preço é o tamanho: um show de vinte faixas dá uns quatro quilobytes de
 * JSON. É uma linha num armazém de aparelho que guarda histórico de anime e
 * lista de servidor de cinema.
 *
 * ── Só no aparelho ────────────────────────────────────────────────────────
 *
 * Nada disto vai para o servidor, pela mesma regra do `anime/guardados.ts`: é
 * preferência de aparelho, não coisa do casal. Quem ouve no celular parou onde
 * parou no celular, e no computador a barra pode muito bem estar noutra faixa.
 *
 * ── Por que a conferência é tão dura ──────────────────────────────────────
 *
 * O que volta do armazém é um texto escrito por uma versão anterior do app, ou
 * escrito pela metade porque o aparelho desligou no meio. Quem o lê é o
 * provedor, no momento em que monta -- e o provedor embrulha o app inteiro:
 * um `undefined.titulo` ali não estraga a barra, estraga a casa toda, e
 * estraga na primeira tela, antes de qualquer coisa aparecer.
 *
 * Então cada campo que o tocador vai encostar é conferido, e qualquer coisa
 * fora do lugar devolve `null` -- que é "começa do zero", e do zero o tocador
 * já sabe começar. A regra é a do `guardados.ts`: perder onde parou é chato,
 * não abrir é pior.
 *
 * O `v1` na chave é pelo mesmo motivo. No dia em que a `Faixa` ganhar um campo
 * obrigatório, os guardados velhos passam a não valer -- e o certo é que não
 * valham mesmo, calados. A marca de versão é para isso ser escolha e não
 * acidente.
 */

import type { EmCartaz, Gravacao } from './acervo';
import type { Faixa } from '../livros/audiolivro';

/** A chave no armazém do aparelho. Ver o `v1` no cabeçalho. */
export const CHAVE_ONDE_PAROU = 'musica_onde_parou_v1';

/**
 * Teto do que se aceita como posição: vinte e quatro horas.
 *
 * O mesmo do audiolivro. Não é para caber nenhuma faixa de verdade -- a maior
 * do `etree` não passa de uma hora --, é para um número absurdo vindo de um
 * guardado estragado não virar um `currentTime` absurdo.
 */
const TETO_MS = 86_400_000;

/** O que o tocador guarda ao sair, e reencontra ao voltar. */
export interface OndeParou {
  /** O item e os nomes, inteiros: ver o cabeçalho. */
  cartaz: EmCartaz;
  /** Em que faixa do item, que não é o `gravacao.indice`: aquele é a procurada. */
  naFaixa: number;
  /** Em que milissegundo da faixa. */
  ms: number;
}

const texto = (v: unknown): v is string => typeof v === 'string';
const numero = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const objeto = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

function faixaValida(v: unknown): v is Faixa {
  return (
    objeto(v) &&
    numero(v.numero) &&
    texto(v.titulo) &&
    // Sem endereço não há o que tocar, e uma faixa muda no meio da lista
    // deixaria a lista da folha com um buraco que ninguém explica.
    texto(v.url) &&
    v.url !== '' &&
    numero(v.duracaoMs)
  );
}

function gravacaoValida(v: unknown): v is Gravacao {
  return (
    objeto(v) &&
    texto(v.iaId) &&
    Array.isArray(v.faixas) &&
    v.faixas.length > 0 &&
    v.faixas.every(faixaValida) &&
    /* O `indice` é a faixa procurada, e o `noCartaz` o compara com a faixa de
       agora para decidir de onde vêm os nomes. Fora da lista ele não estoura,
       só nunca casa -- e aí o nome do Last.fm não voltaria nunca. */
    numero(v.indice) &&
    Number.isInteger(v.indice) &&
    v.indice >= 0 &&
    v.indice < v.faixas.length &&
    texto(v.album) &&
    texto(v.autoria) &&
    texto(v.capaUrl)
  );
}

function cartazValido(v: unknown): v is EmCartaz {
  return (
    objeto(v) &&
    gravacaoValida(v.gravacao) &&
    texto(v.titulo) &&
    texto(v.artista) &&
    texto(v.capaUrl)
  );
}

/** O texto a gravar no armazém. O par do `lerOndeParou`, e o motivo de viverem juntos. */
export function guardarOndeParou(cartaz: EmCartaz, naFaixa: number, ms: number): string {
  return JSON.stringify({
    cartaz,
    naFaixa,
    ms: Math.floor(Math.min(Math.max(ms, 0), TETO_MS)),
  });
}

/**
 * O que estava tocando, ou `null` se não houver nada de que se fiar.
 *
 * Aceita `null` de entrada de propósito: é o que o armazém devolve quando a
 * chave não existe, e quem chama não deveria precisar de um `if` para isso.
 */
export function lerOndeParou(cru: string | null | undefined): OndeParou | null {
  if (!cru) return null;

  let lido: unknown;
  try {
    lido = JSON.parse(cru);
  } catch {
    return null;
  }
  if (!objeto(lido)) return null;

  const { cartaz, naFaixa, ms } = lido;
  if (!cartazValido(cartaz)) return null;
  if (!numero(naFaixa) || !Number.isInteger(naFaixa)) return null;
  if (naFaixa < 0 || naFaixa >= cartaz.gravacao.faixas.length) return null;
  if (!numero(ms)) return null;

  return { cartaz, naFaixa, ms: Math.floor(Math.min(Math.max(ms, 0), TETO_MS)) };
}
